import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate, getStatusColor } from "@/utils/format";

import { Route as ApplyPartnershipRoute } from "@/routes/_app/_auth/dashboard/_layout.partnerships.apply";
import { Button } from "@/components/ui/button";
import { Doc } from "@cvx/_generated/dataModel";
import { Loader2 } from "lucide-react";
import { Route as PartnershipIdRoute } from "@/routes/_app/_auth/partnerships/$partnershipId";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc";
import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { usePaginatedQuery } from "convex/react";

// Type definitions for partnership data
type PartnershipContract = Doc<"creatorContracts"> & {
  type: "contract";
  campaign: Doc<"campaigns"> | null;
};

type PartnershipApplication = Doc<"creatorApplications"> & {
  type: "application";
  campaign: Doc<"campaigns"> | null;
};

type Partnership = PartnershipContract | PartnershipApplication;

export default function CreatorPartnerships() {
  const navigate = useNavigate();

  const {
    results: contracts,
    status: contractsStatus,
    loadMore: loadMoreContracts,
  } = usePaginatedQuery(
    api.partnerships.getPaginatedCreatorPartnershipsForCreator,
    { type: "contracts" },
    { initialNumItems: 10 },
  );

  const {
    results: applications,
    status: applicationsStatus,
    loadMore: loadMoreApplications,
  } = usePaginatedQuery(
    api.partnerships.getPaginatedCreatorPartnershipsForCreator,
    { type: "applications" },
    { initialNumItems: 10 },
  );

  useEffect(() => {
    if (
      contractsStatus !== "LoadingFirstPage" &&
      applicationsStatus !== "LoadingFirstPage" &&
      contracts?.length === 0 &&
      applications?.length === 0
    ) {
      navigate({ to: ApplyPartnershipRoute.fullPath });
    }
  }, [contracts, applications, contractsStatus, applicationsStatus, navigate]);

  const handlePartnershipClick = (partnership: Partnership) => {
    if (
      partnership.type === "application" &&
      (partnership.status === "rejected" || partnership.status === "pending")
    ) {
      navigate({
        to: ApplyPartnershipRoute.fullPath,
        search: {
          selectedAppId: partnership.appId,
        },
      });
    } else {
      navigate({
        to: PartnershipIdRoute.fullPath,
        params: { partnershipId: partnership._id },
      });
    }
  };

  const renderTable = (
    items: Partnership[] | undefined,
    status: "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted",
    loadMore: () => void,
  ) => (
    <div className="rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-border bg-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                App Campaign
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                Campaign Type
              </th>
              <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                {items?.[0] && items[0].type === "contract"
                  ? "Start Date"
                  : "Application Date"}
              </th>
              {items?.[0] && items[0].type === "contract" && (
                <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                  End Date
                </th>
              )}
              <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {status === "LoadingFirstPage" ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center">
                  <Loader2 className="animate-spin" />
                </td>
              </tr>
            ) : items && items.length > 0 ? (
              items.map((item: Partnership) => (
                <tr
                  key={item._id}
                  className="cursor-pointer hover:bg-secondary/50"
                  onClick={() => handlePartnershipClick(item)}
                >
                  <td className="px-6 py-4 text-sm text-primary">
                    {item.campaign?.name || "Unknown Campaign"}
                  </td>
                  <td className="px-6 py-4 text-sm text-primary">
                    {item.campaign?.type ? (
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.campaign.type === "CiR"
                            ? "bg-blue-500/10 text-blue-500"
                            : item.campaign.type === "challenge"
                              ? "bg-green-500/10 text-green-500"
                              : "bg-purple-500/10 text-purple-500"
                        }`}
                      >
                        {item.campaign.type === "CiR"
                          ? "Creator in Residence"
                          : item.campaign.type === "challenge"
                            ? "Challenge"
                            : "Influencer"}
                      </span>
                    ) : (
                      <span className="text-primary/60 text-xs">
                        Unknown Type
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-primary">
                    {formatDate(
                      new Date(
                        item.type === "contract"
                          ? item.startDate
                          : item._creationTime,
                      ).toISOString(),
                    )}
                  </td>
                  {item.type === "contract" && (
                    <td className="px-6 py-4 text-sm text-primary">
                      {formatDate(new Date(item.endDate).toISOString())}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={cn("font-medium", getStatusColor(item.status))}
                    >
                      {item.status === "pendingApproval" &&
                      item.type === "contract"
                        ? "Pending Your Approval"
                        : item.status
                            .split("_")
                            .map(
                              (word: string) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(" ")}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-primary/60"
                >
                  No items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {status === "CanLoadMore" && (
        <div className="flex justify-center p-4 border-t border-border">
          <Button variant="outline" onClick={() => loadMore()}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );

  const hasContracts =
    contractsStatus !== "LoadingFirstPage" && contracts?.length > 0;
  const hasApplications =
    applicationsStatus !== "LoadingFirstPage" && applications?.length > 0;

  // If neither exists, useEffect will handle redirect
  if (!hasContracts && !hasApplications) {
    return null;
  }

  // If only one type exists, render just that content without tabs
  if (hasContracts && !hasApplications) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex justify-end">
            <Button
              onClick={() => navigate({ to: ApplyPartnershipRoute.fullPath })}
            >
              Apply for Partnership
            </Button>
          </div>
          {renderTable(contracts as Partnership[], contractsStatus, () =>
            loadMoreContracts(10),
          )}
        </div>
      </div>
    );
  }

  if (!hasContracts && hasApplications) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex justify-end">
            <Button
              onClick={() => navigate({ to: ApplyPartnershipRoute.fullPath })}
            >
              Apply for Partnership
            </Button>
          </div>
          {renderTable(applications as Partnership[], applicationsStatus, () =>
            loadMoreApplications(10),
          )}
        </div>
      </div>
    );
  }

  // If both exist, render the tabs
  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        <div className="flex justify-end">
          <Button
            onClick={() => navigate({ to: ApplyPartnershipRoute.fullPath })}
          >
            Apply for Partnership
          </Button>
        </div>

        <Tabs defaultValue="contracts" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="contracts">Active Contracts</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
          </TabsList>

          <TabsContent value="contracts" className="mt-0">
            {renderTable(contracts as Partnership[], contractsStatus, () =>
              loadMoreContracts(10),
            )}
          </TabsContent>

          <TabsContent value="applications" className="mt-0">
            {renderTable(
              applications as Partnership[],
              applicationsStatus,
              () => loadMoreApplications(10),
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
